import { t } from '@/features/i18n/utils';
import { WorkspaceBreadcrumbs } from '@/features/workspaces/components/workspace-breadcrumbs';
import { createFileRoute } from '@tanstack/react-router';
import { BreadcrumbLink, BreadcrumbPage } from '@/ui/components/breadcrumb';
import {
  Header,
  HeaderChatButton,
  HeaderBackButton,
  HeaderNextNavButton,
  HeaderNav,
  HeaderPrevNavButton,
  useHeaderNav,
} from '@/ui/components/header';
import { StandardDeviationsDetailPage } from '@/features/standard-deviations/components/standard-deviations-detail-page';
import { useFilteredStandardDeviationGroups } from '@/features/standard-deviations/helpers';
import { findStandardDeviationGroup } from '@/features/standard-deviations/utils';
import { z } from 'zod';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/_layout/(main)/standard-deviations/$deviationId',
)({
  staticData: { chatSurface: 'standard_deviation' },
  component: RouteComponent,
  validateSearch: z.object({
    commentId: z.string().optional(),
    statementId: z.string().optional(),
    comments: z.boolean().optional(),
  }),
});

function RouteComponent() {
  const { workspaceId, projectId, deviationId } = Route.useParams();
  const { statementId } = Route.useSearch();

  const filteredGroups = useFilteredStandardDeviationGroups();
  const group = findStandardDeviationGroup(filteredGroups, deviationId, statementId);

  const navItems = filteredGroups.map((g) => ({ id: g.groupKey }));
  const { handlePrevious, handleNext, isPreviousDisabled, isNextDisabled, currentIndex } =
    useHeaderNav(navItems, group?.groupKey ?? deviationId, (id) => ({
      to: '/project/$projectId/workspaces/$workspaceId/standard-deviations/$deviationId',
      params: { projectId, workspaceId, deviationId: id },
    }));

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/workspaces/$workspaceId/standard-deviations"
          params={{ projectId, workspaceId }}
        />
        <WorkspaceBreadcrumbs>
          <BreadcrumbLink
            to="/project/$projectId/workspaces/$workspaceId/standard-deviations"
            params={{ projectId, workspaceId }}
          >
            {t({ en: 'Technical compliance', nb: 'Teknisk samsvar' })}
          </BreadcrumbLink>
          <BreadcrumbPage className="tabular-nums">#{currentIndex + 1}</BreadcrumbPage>
        </WorkspaceBreadcrumbs>
        <HeaderNav>
          <HeaderPrevNavButton onClick={handlePrevious} disabled={isPreviousDisabled} />
          <HeaderNextNavButton onClick={handleNext} disabled={isNextDisabled} />
        </HeaderNav>
        <HeaderChatButton />
      </Header>
      <StandardDeviationsDetailPage />
    </>
  );
}
